import { Box, CircularProgress, Stack } from "@mui/material";
import { ChartGrid } from "src/components/monitoring/axisLegends/ChartGrid";
import { EmptyDataView } from "src/components/monitoring/senec-chart-wrapper/EmptyDataView";
import { NoSelectionView } from "src/components/monitoring/senec-chart-wrapper/NoSelectionView";
import { ReactNode } from "react";
import { ChartErrorView } from "src/components/monitoring/senec-chart-wrapper/ChartErrorView";

export interface SenecChartWrapperProps {
    /**
     * The child components to render within the SenecChartWrapper.
     */
    children: ReactNode;

    /**
     * A flag indicating whether the chart is in a loading state.
     */
    isLoading: boolean;

    /**
     * A flag indicating whether the chart data is empty.
     */
    emptyData: boolean;

    /**
     * The message to display when there is empty chart data.
     */
    emptyDataViewMsg: string;

    /**
     * The image to display when there is empty chart data.
     */
    emptyDataViewImg?: ReactNode;

    /**
     * A flag indicating whether there is no current selection of chart legend types.
     */
    noSelection: boolean;

    /**
     * The message to display when there is no current selection.
     */
    noSelectionViewMsg?: string;

    /**
     * The image to display when there is no current selection.
     */
    noSelectionViewImg?: ReactNode;

    /**
     * An optional error object that may be provided if an error occurs.
     */
    error?: Error;

    /**
     * An optional error message string to display in case of an error.
     */
    errorMsg?: string;

    /**
     * An optional error code string to display in case of an error.
     */
    errorCode?: string;

    /**
     * The image to display in case of an error.
     */
    errorViewImg?: ReactNode;

    /**
     * An optional function to handle mouse leave.
     */
    onMouseLeave?(): void;
    /**
     * An optional boolean
     */
    noFixedHeight?: boolean;
}

/**
 * This component acts as a wrapper for the `SenecLineChart` and `SenecStackedBarChart`, providing loading, error, and empty state views.
 */
export const SenecChartWrapper = ({
    children,
    isLoading,
    emptyData,
    emptyDataViewMsg,
    emptyDataViewImg,
    noSelection = false,
    noSelectionViewMsg,
    noSelectionViewImg,
    error,
    errorMsg,
    errorCode,
    errorViewImg,
    onMouseLeave,
    noFixedHeight = false,
}: SenecChartWrapperProps) => {
    let chartContent;

    if (isLoading) {
        chartContent = (
            <>
                <Box data-testid={"loading-box"} height={"100%"} alignItems={"center"} display={"flex"}>
                    <CircularProgress color="primary" />
                </Box>
                <ChartGrid />
            </>
        );
    } else if (error) {
        chartContent = <ChartErrorView message={errorMsg ?? "Unknown Error"} errorCode={errorCode} img={errorViewImg} />;
    } else if (emptyData) {
        chartContent = <EmptyDataView message={emptyDataViewMsg} img={emptyDataViewImg} />;
    } else {
        chartContent = (
            <>
                <Stack display={{ display: noSelection ? "block" : "none" }}>
                    <NoSelectionView message={noSelectionViewMsg ?? ""} img={noSelectionViewImg} />
                </Stack>
                <Stack width="100%" display={{ display: noSelection ? "none" : "block" }}>
                    {children}
                </Stack>
            </>
        );
    }

    return (
        <Stack width="100%">
            <Box
                position="relative"
                data-testid={"monitor-chart-wrapper"}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    height: noFixedHeight && !isLoading ? "auto" : "346px",
                    m: "16px",
                    mb: "50px",
                    paddingRight: "20px",
                }}
                onMouseLeave={onMouseLeave}
            >
                {chartContent}
            </Box>
        </Stack>
    );
};
